import { LevelType } from '../types/Level';
import { AIError } from '../ai/types';
import { BatchOrder, Candidate } from './types';
import { Identity, candidateLabel } from './rules';

export const ORDERED_TYPES: LevelType[] = [
  LevelType.WhatsTheSong,
  LevelType.KnownSentence,
  LevelType.WhatsInThePicture,
  LevelType.WhereIsThisLocated,
  LevelType.CategoryWords,
  LevelType.WordSearch,
];

export const LEVEL_TYPE_LABELS: Record<LevelType, string> = {
  [LevelType.WhatsTheSong]: "What's the song",
  [LevelType.KnownSentence]: 'Known sentence',
  [LevelType.WhatsInThePicture]: "What's in the picture",
  [LevelType.WhereIsThisLocated]: 'Where is this located',
  [LevelType.CategoryWords]: 'Category words',
  [LevelType.WordSearch]: 'Word search',
};

// Extra candidates so a level that fails a rule check can be replaced without another call.
export const withSpare = (count: number): number => (count <= 0 ? 0 : count + Math.ceil(count * 0.3));

export const PLAN_SCHEMA: Record<string, unknown> = {
  type: 'object',
  additionalProperties: false,
  required: ['candidates'],
  properties: {
    candidates: {
      type: 'array',
      items: {
        type: 'object',
        additionalProperties: false,
        required: ['levelType', 'sentence', 'locationName', 'category', 'words', 'context', 'difficulty', 'difficultyReason'],
        properties: {
          levelType: { type: 'string', enum: ORDERED_TYPES },
          sentence: { type: 'string' },
          locationName: { type: 'string' },
          category: { type: 'string' },
          words: { type: 'array', items: { type: 'string' } },
          context: { type: 'string' },
          difficulty: { type: 'integer' },
          difficultyReason: { type: 'string' },
        },
      },
    },
  },
};

// What makes a category worth playing in this game. Shared by both category types.
const ISRAELI_CATEGORY =
  'The category must be rooted in Israeli life or culture: something an Israeli smiles at in recognition and a foreigner would not get. ' +
  'Never a generic textbook set that could come from any country, such as days of the week, months, colors, cutlery, furniture, animals, fruits or body parts.';

// The two category types are played differently, so they need different kinds of category.
// Category words: the player must think of the words. Word search: the player only has to spot them.
const RECALL_CATEGORY =
  'The player sees only the category name and has to come up with the words from memory and type them. ' +
  'So the category must be a closed, well-known set with definite members: once an Israeli reads its name, the answers are a matter of recall, not of taste or opinion ' +
  '(for example "שבעת המינים", "מלחמות ישראל", "חגי תשרי", "שבטי ישראל", "ראשי ממשלה"). ' +
  'The test: two Israelis asked separately would write the same list. ' +
  'Never an open theme where hundreds of answers would be just as right (salad ingredients, things you take to the beach, snacks), ' +
  'because the player cannot know which of them this level wants; such a theme belongs in WordSearch. ' +
  'The words must be real members of the set, the best-known ones first.';

const FIND_CATEGORY =
  'The words are hidden in an 8x8 letter grid and are shown to the player, who only has to find them, never to guess them. ' +
  'So the category should be a broad, open theme from Israeli daily life with far more possible members than the level uses, and the words are a fun, recognizable sample of it ' +
  '(for example "מה שמים בסלט", "דברים שלוקחים לים", "מה יש בפיתה", "חטיפים של פעם", "דברים שלוקחים למילואים", "מה אומרים בשוק"). ' +
  'Never a closed set with a short fixed list that a player could recite (the seven species, the tribes, the wars); such a set belongs in CategoryWords. ' +
  'The words must be the specific things Israelis associate with that theme, not dictionary members of a general set.';

const TYPE_RULES: Record<LevelType, string> = {
  [LevelType.WhatsTheSong]:
    'The player sees an illustration hinting at a famous Israeli song and types the answer. Fill "sentence" with the answer and "context" with the performing artist. ' +
    'Spread the songs across eras: about a third from the last 5 years (current hits included), about a third from 2005 to 2020, and at most a third older classics from before 2000. ' +
    'No two songs by the same artist. Mix the genres as well: pop, Mizrahi, rock, hip-hop, Eurovision entries and children\'s songs.',
  [LevelType.KnownSentence]:
    'The player sees an illustration that is a visual riddle for a famous Hebrew idiom, proverb or pop-culture quote. Fill "sentence" with the saying and "context" with its meaning in a few words.',
  [LevelType.WhatsInThePicture]:
    'The player sees a photo of an object, brand, snack or cultural item that Israelis recognize immediately. Fill "sentence" with its name and "context" with one line saying what it is.',
  [LevelType.WhereIsThisLocated]:
    'The player sees a photo of a specific landmark or well-known place in Israel (never just a city). Fill "locationName" with the landmark, "sentence" with the city or area it is in, and "context" with one line about it.',
  [LevelType.CategoryWords]:
    RECALL_CATEGORY + ' ' + ISRAELI_CATEGORY +
    ' Fill "category" and "words" with 5 to 7 short single Hebrew words. Together the words must use at most 16 different letters, so prefer short words that share letters.',
  [LevelType.WordSearch]:
    FIND_CATEGORY + ' ' + ISRAELI_CATEGORY +
    ' Fill "category" and "words" with 8 different Hebrew words of 2 to 8 letters each, best first: the level uses exactly 6, and the last two are spares in case a word cannot be used.' +
    ' Hebrew letters only: no geresh, apostrophes, hyphens, digits, Latin letters or spaces inside a word.',
};

const existingSection = (existing: Identity[]): string => {
  const lines = ORDERED_TYPES.map(type => {
    const labels = existing.filter(item => item.levelType === type).map(candidateLabel);
    return `${type}:\n${labels.length > 0 ? labels.join('\n') : '(none yet)'}`;
  });
  return lines.join('\n\n');
};

export const buildPlanPrompt = (order: BatchOrder, existing: Identity[], rejectedLabels: string[]): string => {
  const requested = ORDERED_TYPES.filter(type => (order.counts[type] || 0) > 0);

  const requestLines = requested.map(type => `- ${type}: ${withSpare(order.counts[type] || 0)} candidates. ${TYPE_RULES[type]}`);
  const theme = order.theme.trim();

  return `You are planning new levels for "RoadIL", a Hebrew puzzle game for an Israeli audience. Every level must be something Israelis know well.

Produce these candidates:
${requestLines.join('\n')}

Rules for every candidate:
- Write all game text in Hebrew without niqqud. "difficultyReason" is also in Hebrew.
- For types with a typed answer, the answer may use at most 16 different letters (final letters count as their regular form). Prefer shorter answers.
- Match the wording style and format of the existing answers of the same type listed below.
- Never repeat or closely paraphrase anything in the existing or rejected lists, and do not repeat yourself within this batch.
- "difficulty" is an integer from 1 to 5: 1 means almost every Israeli knows it, 5 means only enthusiasts do. Give the reason in "difficultyReason".
- Fields that a type does not use must be an empty string, or an empty list for "words".
- Vary the candidates: different eras, artists, regions and topics.

Theme: ${theme || 'none'}${theme ? ' (every candidate should fit this theme)' : ''}

Existing levels, by type:
${existingSection(existing)}

Rejected before, do not propose again:
${rejectedLabels.length > 0 ? rejectedLabels.join('\n') : '(none)'}`;
};

const text = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

const cleanWords = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  const words: string[] = [];
  value.forEach(item => {
    const word = text(item);
    if (word && words.indexOf(word) === -1) words.push(word);
  });
  return words;
};

const clampDifficulty = (value: unknown): number => {
  const number = typeof value === 'number' && isFinite(value) ? Math.round(value) : 1;
  return Math.min(5, Math.max(1, number));
};

export const parsePlan = (json: unknown): Candidate[] => {
  const list = json && typeof json === 'object' ? (json as { candidates?: unknown }).candidates : undefined;
  if (!Array.isArray(list)) {
    throw new AIError('claude', 'Claude returned a plan in an unexpected format. Retry.', true);
  }

  const candidates: Candidate[] = [];
  list.forEach(item => {
    if (!item || typeof item !== 'object') return;
    const raw = item as Record<string, unknown>;
    const levelType = raw.levelType as LevelType;
    if (ORDERED_TYPES.indexOf(levelType) === -1) return;

    candidates.push({
      levelType,
      sentence: text(raw.sentence),
      locationName: text(raw.locationName),
      category: text(raw.category),
      words: cleanWords(raw.words),
      context: text(raw.context),
      difficulty: clampDifficulty(raw.difficulty),
      difficultyReason: text(raw.difficultyReason),
    });
  });
  return candidates;
};
